<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import { EmailSenderFormSubmitEventPayload } from '@Evaluation/Estimations/Components/EmailSender'
    import type { EmailSenderViewModel } from '@Evaluation/Estimations/Components/EmailSender'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: EmailSenderViewModel
    }>()

    const emit = defineEmits<{
        (e: 'form-submit', payload: EmailSenderFormSubmitEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const sending = computed(() => vm.value.sending)
    const recipientIds = computed(() => vm.value.recipientIds)

    const emailSubject = computed<string>({
        get: () => vm.value.emailSubject,
        set: val => { vm.value.emailSubject = val }
    })

    const emailBody = computed<string>({
        get: () => vm.value.emailBody,
        set: val => { vm.value.emailBody = val }
    })

    const emailBodyError = computed(() =>
        emailBody.value.indexOf('{url}') === -1
            ? '{url} is required in the message.'
            : null
    )

    // ---------------- actions ----------------

    function handleFormSubmit()
    {
        vm.value.sending = true

        const payload = new EmailSenderFormSubmitEventPayload(
            emailSubject.value,
            emailBody.value,
            recipientIds.value
        )

        emit('form-submit', payload)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask modal-mask-front" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Send email ({{recipientIds.length}})</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <form id="email-sender-form" @submit.prevent="handleFormSubmit">
                            <div class="form-group">
                                <label>Subject:</label>
                                <input type="text" class="form-control" v-model.trim="emailSubject" required />
                            </div>
                            <div class="form-group">
                                <textarea rows="5" class="form-control" v-model.trim="emailBody" required />
                                <div class="error">{{emailBodyError}}</div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                        <button type="submit" form="email-sender-form" :disabled="sending" class="btn btn-primary">Send email</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>